// src/components/admin/design/NavbarPreview.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { NAVBAR_VARIANTS } from "@/components/store/sections/navbar";
import { resolveCurrentSubdomain } from "@/components/store/sections/navbar/brandingConfig";
import { useDetectedAdminTheme } from "./UniversalProductCardPreview";

export interface NavbarPreviewProps {
  variantId: string;
  primaryColor?: string;
  theme?: "dark" | "light";
  subdomain?: string;
  storeName?: string;
}

const formatStoreBrandTitle = (sub: string): string => {
  const clean = (sub || "").trim();
  if (!clean || clean === "nextlook") return "BuyNara";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

export default function NavbarPreview({
  variantId = "amazon_style",
  primaryColor = "#059669",
  theme: propTheme,
  subdomain: propSubdomain,
  storeName: propStoreName,
}: NavbarPreviewProps) {
  const [activeTheme] = useDetectedAdminTheme(propTheme);
  const isLight = activeTheme === "light";

  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState<number | undefined>(undefined);

  const resolvedSubdomain = resolveCurrentSubdomain(propSubdomain || "buynarabd");
  const resolvedStoreName = propStoreName || formatStoreBrandTitle(resolvedSubdomain);

  const cleanKey = String(variantId || "amazon_style")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  const Comp = NAVBAR_VARIANTS[cleanKey] || NAVBAR_VARIANTS["amazon_style"];

  // Scale full 1240px desktop navbar smoothly to fit the modal preview container so search bar & right icons never crush or clip
  useEffect(() => {
    const DESKTOP_WIDTH = 1240;

    const updateDimensions = () => {
      if (!outerRef.current || !innerRef.current) return;
      const availableWidth = outerRef.current.clientWidth - 16;
      if (availableWidth <= 0) return;

      const nextScale = Math.min(1, Math.max(0.45, availableWidth / DESKTOP_WIDTH));
      setScale(nextScale);

      const rawHeight = innerRef.current.scrollHeight || innerRef.current.offsetHeight;
      if (rawHeight > 0) {
        setScaledHeight(Math.ceil(rawHeight * nextScale));
      }
    };

    updateDimensions();
    const timer = setTimeout(updateDimensions, 120);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateDimensions)
        : null;

    if (resizeObserver && outerRef.current) {
      resizeObserver.observe(outerRef.current);
    }
    if (resizeObserver && innerRef.current) {
      resizeObserver.observe(innerRef.current);
    }

    window.addEventListener("resize", updateDimensions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateDimensions);
      resizeObserver?.disconnect();
    };
  }, [cleanKey]);

  return (
    <div
      ref={outerRef}
      className={`w-full overflow-hidden rounded-2xl border p-2 transition-colors ${
        isLight
          ? "border-slate-200/90 bg-slate-100/80 text-slate-900 shadow-xs"
          : "border-slate-800/90 bg-slate-900/90 text-white shadow-lg"
      }`}
    >
      <div
        className="w-full relative overflow-hidden rounded-xl"
        style={{
          height: scaledHeight ? `${scaledHeight}px` : "auto",
          minHeight: "84px",
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: "1240px",
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
          className="select-none"
        >
          <Comp
            subdomain={resolvedSubdomain}
            storeName={resolvedStoreName}
            primaryColor={primaryColor}
            isPreview={true}
          />
        </div>
      </div>
    </div>
  );
}